/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-application-method.js — Application method step (Phase 2)
 *
 * Doc 6 §11.1 step id `application_method`. Doc 7 §4 application methods.
 *
 * For each selected position, renders a row with radio buttons for the
 * allowed application methods (embroidery / print). Allowed methods come
 * from JRS.Step.PositionPicker.getActiveDescriptor(positionId) — the
 * context-aware lookup that resolves the current scope (per_product
 * category / bundle_wide / multistep) and applies the per-position
 * method restrictions (JRS.MethodRestrictions): hide-embroidery /
 * hide-print product tags and the mix-and-match slot lock-outs. When a
 * position resolves to a single method the row renders it locked with an
 * explanatory note. Falls back to JRS.PositionCatalogue.getById when the
 * picker lookup is unavailable.
 *
 * State path (per-position, plural key — Phase 2 deviation from Doc 6):
 *   state.draft.application_methods = {
 *     left_breast: 'embroidery',
 *     right_sleeve: 'print',
 *     ...
 *   }
 *
 * --- Per-position method (reconciled with Doc 6) ---
 * `draft.application_methods` is a plural per-position map
 * ({ [positionId]: 'embroidery' | 'print' }), matching the cart-payload
 * contract (Doc 2: `Application Method N`, one per position, each
 * mapping to a position-product variant) and `jrs-v2-types.js`
 * (`PositionAssignment.applicationMethod`, per position). Doc 6 §5.3
 * originally listed a singular flow-wide `application_method`; that has
 * been superseded — see the Doc 6 §5.3 change-log entry dated
 * 2026-06-01. The singular field is no longer used anywhere.
 *
 * Defaults (revised 2026-09-01): on entry, each selected position without
 * a method is seeded with the FIRST allowed method (embroidery wherever
 * both exist — method order is embroidery-first). The 2026-06-01 smart
 * default (print for large placements) is withdrawn. Seeding is
 * non-destructive — existing user / cart-edit / resume choices are never
 * overwritten — and silent (no decision telemetry; a default is not a
 * user interaction).
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'application_method';

  var _mountEl = null;
  var _listeners = [];

  // Reference method icons (decorative) + lock glyph for single-method
  // positions. Pulled from docs/reference/customizer-position-method-reference.html.
  var METHOD_ICONS = {
    embroidery: '<svg class="jrs-method-radio__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20 L17 7"/><circle cx="17.5" cy="6.5" r="2"/><path d="M19.5 4.5q3 2 .8 4.4"/></svg>',
    print: '<svg class="jrs-method-radio__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 9V4h10v5"/><rect x="4" y="9" width="16" height="7" rx="2"/><path d="M7 14h10v6H7z"/></svg>'
  };
  var LOCK_ICON = '<svg class="jrs-method-note__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopup.setReassurance(
      'Embroidery is stitched in and hard-wearing. Print is pressed on, best for ' +
      'photos and gradients.'
    );

    var draft = state.draft || {};
    var positions = draft.positions || [];

    if (positions.length === 0) {
      // Guard: position_picker should have populated positions before
      // we ever get here. If somehow not, render an instructive empty
      // state instead of an empty step.
      _renderEmpty();
      return;
    }

    // Smart-default seeding (2026-06-01) — pre-select a sensible method
    // per position (Default Effect) so the step opens resolved rather
    // than blank. NON-DESTRUCTIVE: only positions WITHOUT an existing
    // method get seeded, so user-chosen / cart-edit (originalProperties)
    // / resume values always win. Seeding is internal state — it does
    // NOT fire a decision telemetry event (it's a default, not a user
    // interaction). One dispatch only when something actually changed,
    // so re-entering the step is a no-op.
    var seeded = _seedDefaults(positions, draft.application_methods || {});
    if (seeded.changed) {
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'application_methods', value: seeded.map }
      });
    }

    _render(_orderLikePicker(positions, state), seeded.map, _categoryProgress(state));
    _bindInteractions();
    _syncCta(); // chrome-footer Continue CTA (enabled — all positions seeded)
  }

  /**
   * Render the rows in the SAME order the position_picker listed them.
   * draft.positions is selection order (the sequence the customer tapped),
   * so Large Back could sit above Left Breast here while step 2 showed the
   * reverse. The picker's order is derived, not fixed — alphabetical by
   * name, Left Breast pinned above it, any free position floated to the
   * top, after its filters — so this reads that same resolved catalogue
   * rather than hardcoding a sequence. Anything not in the catalogue (a
   * cart-edit leftover, a scope mismatch) keeps its relative order at the
   * end rather than being dropped. Returns a new array: draft.positions is
   * cart-payload state and is never reordered in place.
   */
  function _orderLikePicker(positions, state) {
    var picker = JRS.Step && JRS.Step.PositionPicker;
    if (!picker || typeof picker._resolveActiveCatalogue !== 'function') return positions;
    var catalogue;
    try { catalogue = picker._resolveActiveCatalogue(state || {}) || []; }
    catch (e) { return positions; }
    if (!catalogue.length) return positions;

    var rank = {};
    for (var c = 0; c < catalogue.length; c++) rank[catalogue[c].id] = c;

    var known = [];
    var unknown = [];
    for (var i = 0; i < positions.length; i++) {
      if (typeof rank[positions[i]] === 'number') known.push(positions[i]);
      else unknown.push(positions[i]);
    }
    known.sort(function (a, b) { return rank[a] - rank[b]; });
    return known.concat(unknown);
  }

  /**
   * Merge smart-default methods into the existing map for any position
   * that lacks one. Returns the merged map plus a `changed` flag so the
   * caller dispatches only when a seed actually happened.
   *
   * @param {string[]} positions
   * @param {Object<string,string>} existing - draft.application_methods
   * @returns {{ map: Object<string,string>, changed: boolean }}
   */
  function _seedDefaults(positions, existing) {
    var map = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) map[k] = existing[k];
    }
    var changed = false;
    for (var i = 0; i < positions.length; i++) {
      var pid = positions[i];
      // Never overwrite an existing, valid choice.
      if (map[pid] === 'embroidery' || map[pid] === 'print') continue;
      map[pid] = _defaultMethodFor(pid);
      changed = true;
    }
    return { map: map, changed: changed };
  }

  /**
   * Resolve a position's default method: the first of its allowed
   * methods. Falls back to 'embroidery' + console.warn for an unknown /
   * typo position id (one not in the catalogue) so a misconfigured
   * draft never crashes the step.
   *
   * @param {string} positionId
   * @returns {'embroidery'|'print'}
   */
  function _defaultMethodFor(positionId) {
    var descriptor = _descriptorFor(positionId);
    var allowed = (descriptor.application_methods && descriptor.application_methods.length)
      ? descriptor.application_methods
      : ['embroidery', 'print'];
    // Operator ruling 2026-09-01: the FIRST allowed method is ALWAYS the
    // default — no per-position preference (the old descriptor.default_method
    // preference made Large Back default to Print). A position locked to one
    // method is naturally covered.
    return allowed[0];
  }

  /**
   * Resolve a position's descriptor for the CURRENT scope with method
   * restrictions applied, via the position picker's context-aware lookup.
   * Falls back to the shop catalogue (then a synthetic both-methods
   * descriptor) so a missing position never crashes the step.
   *
   * @param {string} positionId
   * @returns {Object} descriptor with an application_methods array
   */
  function _descriptorFor(positionId) {
    var d = (JRS.Step && JRS.Step.PositionPicker &&
             typeof JRS.Step.PositionPicker.getActiveDescriptor === 'function')
      ? JRS.Step.PositionPicker.getActiveDescriptor(positionId)
      : null;
    if (d) return d;
    var fromCatalogue = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getById === 'function')
      ? JRS.PositionCatalogue.getById(positionId)
      : null;
    if (fromCatalogue) return fromCatalogue;
    if (window.console && typeof console.warn === 'function') {
      console.warn(
        'jrs-v2-step-application-method: no descriptor for position "' +
        positionId + '" (not in catalogue?) — falling back to both methods.'
      );
    }
    return { id: positionId, name: positionId, application_methods: ['embroidery', 'print'], default_method: 'embroidery' };
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var draft = state.draft || {};
    var positions = draft.positions || [];
    var methods = draft.application_methods || {};

    var missing = [];
    for (var i = 0; i < positions.length; i++) {
      var pid = positions[i];
      var m = methods[pid];
      if (m !== 'embroidery' && m !== 'print') { missing.push(pid); continue; }
      // Reject a stored method the position no longer allows (stale draft /
      // cart-edit / resume into a restricted bundle slot).
      var descriptor = _descriptorFor(pid);
      var allowed = (descriptor.application_methods && descriptor.application_methods.length)
        ? descriptor.application_methods
        : ['embroidery', 'print'];
      if (allowed.indexOf(m) === -1) missing.push(pid);
    }

    if (missing.length > 0) {
      _showError('Please pick an application method for every position.');
      _highlightMissing(missing);
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
        step_id: STEP_ID,
        field_id: 'application_methods',
        reason: 'incomplete_per_position',
        missing_positions: missing
      });
      return { valid: false, errors: { application_methods: 'incomplete_per_position' } };
    }
    _clearError();
    return { valid: true };
  }

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  function _renderEmpty() {
    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--application-method" data-jrs-step="application_method">' +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title">Choose application method</h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
      '    <p>No positions selected — please go back and pick at least one.</p>' +
      '  </section>' +
      '</div>';
  }

  // per_product: returns the category context (name + "{n} of {N}" cursor) for
  // the header; bundle_wide returns { perProduct: false } so the header renders
  // unchanged. Mirrors position_picker._headerInfo (spec D5).
  function _categoryProgress(state) {
    var draft = (state && state.draft) || {};
    if (draft.branding_strategy !== 'per_product') return { perProduct: false };
    var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
      ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    var cat = active[cursor];
    return {
      perProduct: true,
      categoryName: (cat && cat.name) || 'this product',
      current: cursor + 1,
      total: active.length || 1
    };
  }

  function _render(positions, methods, headerInfo) {
    var copy = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getMethodCopy === 'function')
      ? JRS.PositionCatalogue.getMethodCopy()
      : null;

    var rowsHtml = '';
    for (var i = 0; i < positions.length; i++) {
      var pid = positions[i];
      var descriptor = _descriptorFor(pid);
      var chosen = methods[pid] || null;
      rowsHtml += _renderRow(descriptor, chosen, i);
    }

    headerInfo = headerInfo || { perProduct: false };
    // per_product: a "{n} of {N}" progress pill above the title + the category
    // name as the sub-label (which product we're applying methods to). Reuses
    // the existing .jrs-step__progress-pill pattern (spec D5).
    var pillHtml = headerInfo.perProduct
      ? '<p class="jrs-step__progress-pill">' + headerInfo.current + ' of ' + headerInfo.total + '</p>'
      : '';
    // per_product keeps the category name as context; the default "pre-select"
    // subtitle is omitted (UI polish).
    var subtitleHtml = headerInfo.perProduct
      ? '<p class="jrs-step__subtitle">' + _escape(headerInfo.categoryName) + '</p>'
      : '';

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--application-method" data-jrs-step="application_method">' +
      '  <header class="jrs-step__header' + (headerInfo.perProduct ? ' jrs-step__header--per-position' : '') + '">' +
           pillHtml +
      '    <h2 class="jrs-step__title">How should we apply your logo?</h2>' +
           subtitleHtml +
      '  </header>' +
      '  <section class="jrs-step__body">' +
           _renderBundleWideNote() +
           _renderHelp(copy) +
      '    <ol class="jrs-method-rows" role="list">' + rowsHtml + '</ol>' +
      '    <p data-jrs-method-error class="jrs-step__error" hidden role="alert"></p>' +
      '  </section>' +
      '</div>';
  }

  /**
   * True when the bundle_wide expectation note is actually relevant: at least
   * one SELECTED position where the garments DIFFER — the position offers a
   * choice (≥2 methods in the merged view) AND at least one garment is locked to
   * a single method (its own restriction), so the customer's pick will be
   * overridden for that garment. Returns false when every selected position is
   * "both methods for every garment" (e.g. Left Breast — nothing is forced) and
   * when a position is uniformly locked (the step already shows only that one
   * method, so there's nothing to warn about). Only meaningful for bundle_wide.
   * Exposed as a test seam.
   */
  function _bundleWideNeedsNote(state) {
    var draft = (state && state.draft) || {};
    if (draft.branding_strategy !== 'bundle_wide') return false;
    var positions = draft.positions || [];
    var children = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                    window.JRS_PDP_CONTEXT.bundle_children) || [];
    if (!positions.length || !children.length ||
        !JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') {
      return false;
    }
    // Slot context (2026-08-01, control parity): evaluate each garment with its
    // REAL bundle slot (child index + 1) plus the parent's mix&match flag, so
    // the note tracks the same slot rules the submit path now applies — e.g.
    // Large Back on slots 1-2 no longer locks (ALLOW_LARGE_BACK_EMBROIDERY),
    // while slot-3 outerwear or slots-1-2 Large Front still force a method and
    // still warrant the note.
    var parentTags = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                      window.JRS_PDP_CONTEXT.productTags) || [];
    var parentMix = typeof JRS.MethodRestrictions.isMixAndMatchBundle === 'function' &&
                    JRS.MethodRestrictions.isMixAndMatchBundle(parentTags);
    for (var i = 0; i < positions.length; i++) {
      var descriptor = _descriptorFor(positions[i]);
      var union = (descriptor.application_methods && descriptor.application_methods.length)
        ? descriptor.application_methods : ['embroidery', 'print'];
      if (union.length < 2) continue; // no choice on this position → nothing to override
      for (var c = 0; c < children.length; c++) {
        var allowed = JRS.MethodRestrictions.getAllowedMethods({
          positionId: descriptor.id,
          productTags: (children[c] && children[c].tags) || [],
          bundleSlot: c + 1,
          mixAndMatch: parentMix,
          baseMethods: union
        });
        if (allowed.length < union.length) return true; // a garment is locked on a choice-position
      }
    }
    return false;
  }

  /**
   * bundle_wide expectation note — rendered ONLY when _bundleWideNeedsNote is
   * true (a selected position where some garment is locked to one method). We
   * honour the customer's choice where the garment allows it, and automatically
   * apply the locked method where it doesn't. '' when no substitution is
   * possible for the current position selection (e.g. Left Breast, both methods
   * available on every garment), and for multistep / per_product.
   */
  function _renderBundleWideNote() {
    var state = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getState === 'function')
      ? JRS.CustomizerPopupState.getState() : {};
    if (!_bundleWideNeedsNote(state)) return '';
    return (
      '<aside class="jrs-method-applyall" data-jrs-method-applyall>' +
      '  <svg class="jrs-method-applyall__icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
      '    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm1.25 8H6.75v-1h.75V8h-.75V7h2v4H9.25v1Z"/>' +
      '  </svg>' +
      '  <p class="jrs-method-applyall__text">Your choice applies to every garment that supports it — you\'ll see the full breakdown before adding to cart.</p>' +
      '</aside>'
    );
  }

  // Drive the shell's chrome-footer Continue CTA. Enabled once every selected
  // position has a method — always true after smart-default seeding, but
  // computed for robustness (and future per-position method restrictions).
  function _syncCta() {
    var pop = JRS.CustomizerPopup;
    if (!pop || typeof pop.setPrimaryCta !== 'function') return;
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var positions = draft.positions || [];
    var methods = draft.application_methods || {};
    var complete = positions.length > 0;
    for (var i = 0; i < positions.length; i++) {
      var m = methods[positions[i]];
      if (m !== 'embroidery' && m !== 'print') { complete = false; break; }
    }
    // Name the screen this actually lands on. The flow sends 2+ positions to
    // logo_strategy and a single position straight to logo_picker
    // (jrs-v2-flow-multistep.js nextStep: `positions.length >= 2`), so the
    // threshold here is that same 2. Read at press time from the live
    // selection; the step re-mounts on every entry, so going back to step 2
    // and changing the count re-runs this.
    pop.setPrimaryCta({
      label: positions.length >= 2 ? 'Continue to your logos' : 'Add your logo',
      disabled: !complete,
      onClick: _onContinue
    });
  }

  // Method-comparison assets (print vs embroidery images + captions), bridged
  // from theme settings via the [data-jrs-method-compare] JSON tag emitted at the
  // popup shell mount (jrs-v2-customizer-popup.liquid). Read once and cached;
  // `undefined` = not read yet, `null` = read but nothing usable.
  var _methodCompare;
  function _readMethodCompare() {
    if (typeof _methodCompare !== 'undefined') return _methodCompare;
    _methodCompare = null;
    try {
      if (typeof document === 'undefined' || !document.querySelector) return null;
      var el = document.querySelector('[data-jrs-method-compare]');
      if (!el || !el.textContent) return null;
      var data = JSON.parse(el.textContent);
      if (data && (data.embroidery || data.print)) _methodCompare = data;
    } catch (e) { /* malformed bridge → simply no comparison block */ }
    return _methodCompare;
  }

  /**
   * Two-card print vs embroidery visual comparison, inserted at the TOP of the
   * "Which should I choose?" disclosure body. Presentational only. Embroidery
   * first (matches the step's order). Labels reuse the step's _methodLabel;
   * captions + images come from theme settings. An empty image → caption-only
   * card (never a broken <img>). Returns '' when the bridge is absent, so the
   * disclosure falls back to its existing text-only guidance.
   */
  function _renderMethodCompare() {
    var data = _readMethodCompare();
    if (!data) return '';
    return (
      '<div class="jrs-method-compare">' +
        _methodCompareCard('embroidery', data.embroidery || {}) +
        _methodCompareCard('print', data.print || {}) +
      '</div>'
    );
  }

  function _methodCompareCard(method, entry) {
    var image = (entry && typeof entry.image === 'string') ? entry.image : '';
    var caption = (entry && typeof entry.caption === 'string') ? entry.caption : '';
    var alt = (method === 'print')
      ? 'Close-up example of a printed logo'
      : 'Close-up example of an embroidered logo';
    var media = image
      ? '<span class="jrs-method-compare__media">' +
          '<img src="' + _escape(image) + '" alt="' + _escape(alt) +
          '" loading="lazy" decoding="async" width="800" height="500">' +
        '</span>'
      : '';
    return (
      '<div class="jrs-method-compare__card">' +
        media +
        '<span class="jrs-method-compare__label">' + _escape(_methodLabel(method)) + '</span>' +
        (caption ? '<span class="jrs-method-compare__caption">' + _escape(caption) + '</span>' : '') +
      '</div>'
    );
  }

  /**
   * "Which should I choose?" disclosure, collapsed by default. Native
   * button + aria-expanded + a hidden body region (not <details>, to
   * keep the chevron-rotation + reduced-motion handling in our own CSS
   * and consistent with the tip-banner / login-nudge button patterns).
   * Returns '' when copy is unavailable so we never render an empty
   * shell. The body is the print/embroidery comparison pair, whose captions
   * come from the theme settings; the two summary paragraphs that used to sit
   * below it were removed (2026-09-13) because they explained each method a
   * second time. No bridge → the comparison renders '' and the disclosure is
   * the toggle alone.
   */
  function _renderHelp(copy) {
    if (!copy || !copy.help || !copy.help.toggle) return '';
    var h = copy.help;
    return (
      '<div class="jrs-method-help" data-jrs-method-help>' +
      '  <button type="button" class="jrs-method-help__toggle"' +
      '          data-jrs-action="method-help-toggle"' +
      '          aria-expanded="false" aria-controls="jrs-method-help-body">' +
      '    <svg class="jrs-method-help__chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6 L8 10 L12 6"/></svg>' +
      '    <span class="jrs-method-help__toggle-text">' + _escape(h.toggle) + '</span>' +
      '  </button>' +
      '  <div class="jrs-method-help__body" id="jrs-method-help-body" aria-hidden="true">' +
            _renderMethodCompare() +
      '  </div>' +
      '</div>'
    );
  }

  /**
   * Bold the method-name prefix (text before the " — " em-dash) so the help
   * body matches the reference's "<strong>Embroidery</strong> — …" styling.
   * Both segments are escaped; only our own <strong> wrapper is literal.
   */
  function _helpLine(text) {
    var sep = ' — ';
    var idx = (typeof text === 'string') ? text.indexOf(sep) : -1;
    if (idx === -1) return _escape(text || '');
    return '<strong>' + _escape(text.slice(0, idx)) + '</strong>' + _escape(text.slice(idx));
  }

  function _renderRow(descriptor, chosen, index) {
    var methodOptions = '';
    for (var j = 0; j < descriptor.application_methods.length; j++) {
      var m = descriptor.application_methods[j];
      var inputId = 'jrs-method-' + descriptor.id + '-' + m;
      // RECOMMENDED tag removed from application methods (operator ruling
      // 2026-09-01) — badges elsewhere in the customiser (e.g. the position
      // picker's pill) are untouched.
      var tagHtml = '';
      var iconHtml = METHOD_ICONS[m] || '';
      methodOptions +=
        '<label class="jrs-method-radio" for="' + _escape(inputId) + '">' +
        '  <input type="radio"' +
        '         id="' + _escape(inputId) + '"' +
        '         name="jrs-method-' + _escape(descriptor.id) + '"' +
        '         class="jrs-method-radio__input"' +
        '         data-jrs-action="method-change"' +
        '         data-jrs-position-id="' + _escape(descriptor.id) + '"' +
        '         data-jrs-position-index="' + index + '"' +
        '         data-jrs-value="' + _escape(m) + '"' +
        '         ' + (chosen === m ? 'checked' : '') + '>' +
              tagHtml +
        '  <span class="jrs-method-radio__head">' +
              iconHtml +
        '    <span class="jrs-method-radio__label">' + _escape(_methodLabel(m)) + '</span>' +
        '  </span>' +
        '</label>';
    }

    // Position thumb: the real metaobject photo (descriptor.image — same image
    // the live theme + position picker use) when present, else the catalogue's
    // garment pictogram.
    var thumbInner = descriptor.image
      ? '<img class="jrs-method-row__thumb-img" src="' + _escape(descriptor.image) + '" alt="" loading="lazy" decoding="async">'
      : (descriptor.icon || '');
    var thumbHtml = thumbInner
      ? '<span class="jrs-method-row__thumb" aria-hidden="true">' + thumbInner + '</span>'
      : '';

    // Single-method positions (e.g. embroidery-only) get an explanatory note.
    // Won't render today (the catalogue allows both methods everywhere) but
    // matches the reference and is ready for the Phase 5 metaobject.
    var noteHtml = '';
    if (descriptor.application_methods.length === 1) {
      noteHtml =
        '<p class="jrs-method-note">' + LOCK_ICON +
        '<span>' + _escape(_methodLabel(descriptor.application_methods[0])) +
        ' only on this position.</span></p>';
    }

    return (
      '<li class="jrs-method-row" data-jrs-method-row="' + _escape(descriptor.id) + '">' +
      '  <div class="jrs-method-row__head">' +
            thumbHtml +
      '    <span class="jrs-method-row__position">' + _escape(descriptor.name) + '</span>' +
      '  </div>' +
      '  <div class="jrs-method-row__options" role="radiogroup" aria-label="Application method for ' + _escape(descriptor.name) + '">' + methodOptions + '</div>' +
            noteHtml +
      '</li>'
    );
  }

  function _methodLabel(m) {
    if (m === 'embroidery') return 'Embroidery';
    if (m === 'print') return 'Print';
    return m;
  }

  function _showError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-method-error]');
    if (err) { err.textContent = msg; err.hidden = false; }
  }

  function _clearError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-method-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
    var rows = _mountEl.querySelectorAll('.jrs-method-row--missing');
    for (var i = 0; i < rows.length; i++) {
      rows[i].classList.remove('jrs-method-row--missing');
    }
  }

  function _highlightMissing(positionIds) {
    if (!_mountEl) return;
    var allRows = _mountEl.querySelectorAll('[data-jrs-method-row]');
    for (var i = 0; i < allRows.length; i++) {
      allRows[i].classList.remove('jrs-method-row--missing');
    }
    for (var j = 0; j < positionIds.length; j++) {
      var row = _mountEl.querySelector('[data-jrs-method-row="' + _cssEscape(positionIds[j]) + '"]');
      if (row) row.classList.add('jrs-method-row--missing');
    }
  }

  // -------------------------------------------------------------------
  // Interactions
  // -------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    var radios = _mountEl.querySelectorAll('[data-jrs-action="method-change"]');
    for (var i = 0; i < radios.length; i++) {
      _bind(radios[i], 'change', _onMethodChange);
    }
    var helpToggle = _mountEl.querySelector('[data-jrs-action="method-help-toggle"]');
    if (helpToggle) _bind(helpToggle, 'click', _onHelpToggle);
  }

  /**
   * Toggle the "Which should I choose?" disclosure. Flips aria-expanded
   * + the body's [hidden] + an `is-open` class the CSS uses to rotate
   * the chevron. No telemetry (a help-expand isn't a config decision).
   */
  function _onHelpToggle(e) {
    var btn = e.currentTarget;
    if (!btn) return;
    var wrap = btn.closest('[data-jrs-method-help]');
    var body = wrap ? wrap.querySelector('.jrs-method-help__body') : null;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    // `.is-open` drives the CSS max-height/opacity transition (no `hidden`,
    // which can't animate). aria-hidden tracks state for assistive tech.
    if (wrap) wrap.classList.toggle('is-open', !expanded);
    if (body) body.setAttribute('aria-hidden', expanded ? 'true' : 'false');
  }

  function _onMethodChange(e) {
    var input = e.currentTarget;
    var positionId = input.getAttribute('data-jrs-position-id');
    var positionIndex = parseInt(input.getAttribute('data-jrs-position-index'), 10);
    var value = input.getAttribute('data-jrs-value');
    if (!positionId || (value !== 'embroidery' && value !== 'print')) return;

    // Defensive: ignore a selection the position doesn't allow. Restricted
    // methods aren't rendered, so this only fires on a tampered/stale DOM.
    var descriptor = _descriptorFor(positionId);
    var allowed = (descriptor.application_methods && descriptor.application_methods.length)
      ? descriptor.application_methods
      : ['embroidery', 'print'];
    if (allowed.indexOf(value) === -1) return;

    var current = JRS.CustomizerPopupState.getState().draft || {};
    var existing = current.application_methods || {};
    var next = {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) next[k] = existing[k];
    }
    next[positionId] = value;

    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'application_methods', value: next }
    });

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'application_method',
      decision_value: value,
      position_id: positionId,
      position_index: isNaN(positionIndex) ? null : positionIndex
    });
  }

  function _onContinue() {
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;

    // The flow orchestrator (Phase 2 commit 9) decides whether to insert
    // logo_strategy (≥2 positions) or skip directly to logo_picker.
    // For self-contained controller behaviour pre-orchestrator we make
    // the same decision here.
    var positions = (state.draft && state.draft.positions) || [];
    var nextStep = positions.length >= 2 ? 'logo_strategy' : 'logo_picker';
    JRS.CustomizerPopup.goToStep(nextStep, { trigger: 'primary_cta' });
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function _cssEscape(s) {
    // Sufficient for our position-id values which are [a-z_] only.
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // -------------------------------------------------------------------
  // Public API + register
  // -------------------------------------------------------------------

  JRS.Step.ApplicationMethod = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    _categoryProgress: _categoryProgress,
    _bundleWideNeedsNote: _bundleWideNeedsNote,
    // Print vs embroidery visual comparison (top of the "Which should I
    // choose?" disclosure) — presentational; reads the [data-jrs-method-compare]
    // settings bridge.
    _renderMethodCompare: _renderMethodCompare
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
